import { useNavigate, useParams } from 'react-router';
import { Plus, FileText, Layers, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

import type { LLMsListItem, LLMsViewType } from '@intone/shared/types';

import { ROUTES } from '@/shared/config';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { EntityList } from '@/widgets/entityList';

import { useShowDialog, UnsavedChangesDialogTexts } from '@/hooks/useShowDialog';
import {
  useDeleteLLM,
  useDuplicateLLM,
  useCreateLLM,
  DEFAULT_MODEL,
  getDefaultStatesConfig,
  getPath,
} from '@/entities/llm';

import { useUnsavedChanges } from '../lib/FormStatusProvider';

type ScriptListProps = {
  scripts: LLMsListItem[];
  isLoading: boolean;
}

export function ScriptList({ scripts, isLoading }: ScriptListProps) {
  const { isDirty, setIsDirty } = useUnsavedChanges();
  const navigate = useNavigate();
  const { id: currentId } = useParams<{ id?: string }>();
  const activeId = currentId ? Number(currentId) : null;

  const createLLM = useCreateLLM();
  const deleteLLM = useDeleteLLM();
  const duplicateLLM = useDuplicateLLM();

  const shouldBlock = isDirty && !deleteLLM.isPending;
  const { showConfirmDialog } = useShowDialog(shouldBlock);

  const handleSelectItem = (item: LLMsListItem) => {
    if (isDirty && !showConfirmDialog(UnsavedChangesDialogTexts.scriptsLeave)) return;
    setIsDirty(false);
    navigate(getPath(item.id.toString()));
  };

  const handleCreateNew = (viewType: LLMsViewType) => {
    if (isDirty && !showConfirmDialog(UnsavedChangesDialogTexts.scriptsCreate)) return;
    setIsDirty(false);

    createLLM.mutate(
      {
        name: 'New LLM',
        model: DEFAULT_MODEL,
        states_config: getDefaultStatesConfig(viewType),
        view_type: viewType,
      },
      {
        onSuccess: ({ llm, message }) => {
          navigate(getPath(llm.id.toString()), { replace: true });
          toast.success(message, { id: 'llm-created' });
        },
        onError: (error: Error) => toast.error(
          `Failed to create LLM${error.message ? ': ' + error.message : ''}`,
          { id: 'llm-create-error' }
        ),
      }
    );
  };

  const handleDelete = (item: LLMsListItem) => {
    if (!window.confirm('Are you sure you want to delete this LLM?')) return;

    if (activeId === item.id) navigate(ROUTES.SCRIPTS.LIST, { replace: true });

    deleteLLM.mutate(item.id, {
      onSuccess: ({message}) => {
        toast.success(message, { id: 'llm-delete-success' });
      },
      onError: (error: Error) => {
        toast.error(`Failed to delete LLM${error.message ? ': ' + error.message : ''}`, {
          id: 'llm-delete-error',
        });
        if (activeId === item.id) navigate(getPath(item.id.toString()), { replace: true });
      },
    });
  };

  const handleDuplicate = (item: LLMsListItem) => {
    if (isDirty && !showConfirmDialog(UnsavedChangesDialogTexts.scriptsDuplicate)) return;
    setIsDirty(false);

    duplicateLLM.mutate(
      {
        llmId: item.id,
        newName: `${item.name} (copy)`,
        view_type: item.view_type,
        model: item.model,
      },
      {
        onSuccess: ({llm, message}) => {
          toast.success(message, { id: 'llm-duplicate-success' });
          navigate(getPath(llm.id.toString()));
        },
        onError: (error: Error) => toast.error(
          `Failed to duplicate LLM${error.message ? ': ' + error.message : ''}`,
          { id: 'llm-duplicate-error' }
        ),
      }
    );
  };

  const createButton = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={isLoading || createLLM.isPending}
        >
          <Plus className="w-3 h-3 mr-2" />
          {createLLM.isPending ? 'Creating...' : 'Create New LLM'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-48">
        <DropdownMenuItem onClick={() => handleCreateNew('single_prompt')}>
          <FileText className="mr-2 h-4 w-4" /> Single Prompt
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCreateNew('double_state')}>
          <Layers className="mr-2 h-4 w-4" /> Double-state
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleCreateNew('template')}>
          <GitBranch className="mr-2 h-4 w-4" /> Template
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <EntityList<LLMsListItem>
      items={scripts}
      selectedId={activeId ?? undefined}
      getId={(s) => s.id}
      onSelect={handleSelectItem}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      renderPrimary={(s) => s.name}
      renderSecondary={(s) => (
        <>
          {s.view_type && (
            <div className="text-detail text-gray-500 truncate mt-0.5">
              {s.view_type.replace(/_/g, ' ')}
            </div>
          )}
          <div className="text-detail text-gray-400 truncate">{s.model}</div>
        </>
      )}
      searchPlaceholder="Search LLMs..."
      searchFilter={(s, q) =>
        s.name.toLowerCase().includes(q) || s.model.toLowerCase().includes(q)
      }
      isLoading={isLoading}
      emptyMessage="No LLMs found"
      createButton={createButton}
    />
  );
}